import React from "react";
import {
  Box, Button, CircularProgress, Dialog, IconButton, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LegacyTokens } from "../../../../theme/b13Tokens";
import { useConfirm } from "../../../../Components/Confirm";

// Batch 7 §7.1 — centred workflow modal. 1100px, dark scrim, internal
// scroll, sticky title + footer. Mirrors PromptLevelModal from Batch 6.
// Title carries a ? help affordance and (optionally) an Open-in-new-tab.
// Objective subheader rendered by the CALLER — the subheader text is
// derived from the workflow's `objective` field and is not part of the
// modal chrome.

export type WorkflowModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  onOpenInNewTab?: () => void;
  onHelp?: () => void;
  saving?: boolean;
  disableSave?: boolean;
  hasUnsavedChanges?: boolean;
  children: React.ReactNode;
};

export default function WorkflowModal(props: WorkflowModalProps) {
  const {
    open, title, onClose, onSave, onOpenInNewTab, onHelp,
    saving = false, disableSave = false, hasUnsavedChanges = false,
    children,
  } = props;

  const confirm = useConfirm();
  const requestClose = async () => {
    if (hasUnsavedChanges) {
      const ok = await confirm({
        title: "Discard unsaved changes?",
        message: "You have unsaved changes. Discard them?",
        confirmText: "Discard",
        cancelText: "Keep editing",
        variant: "warning",
      });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "1100px",
          maxWidth: "calc(100vw - 48px)",
          maxHeight: "calc(100vh - 48px)",
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky title with ? then optional Open-in-new-tab, then × */}
      <Box
        sx={{
          px: 3, py: 2,
          display: "flex", alignItems: "center", gap: 1,
          borderBottom: `1px solid ${LegacyTokens.line}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: LegacyTokens.ink, flexGrow: 1 }}>
          {title}
        </Typography>
        {onHelp && (
          <Tooltip title="Help" arrow>
            <IconButton onClick={onHelp} sx={{ color: LegacyTokens.ink3 }} aria-label="Help">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
        {onOpenInNewTab && (
          <Tooltip title="Open in new tab" arrow>
            <IconButton onClick={onOpenInNewTab} sx={{ color: LegacyTokens.ink3 }} aria-label="Open in new tab">
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={requestClose} aria-label="Close" sx={{ color: LegacyTokens.ink3 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, backgroundColor: LegacyTokens.canvas }}>
        {children}
      </Box>

      <Box
        sx={{
          px: 3, py: 1.5,
          borderTop: `1px solid ${LegacyTokens.line}`,
          display: "flex", justifyContent: "flex-end", gap: 2,
          flexShrink: 0,
          backgroundColor: LegacyTokens.surface,
        }}
      >
        <Button
          onClick={requestClose}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
            color: "#D32F2F",
            "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={disableSave || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
            color: "#1A73E8",
            "&:hover": { backgroundColor: "rgba(26, 115, 232, 0.06)" },
            "&.Mui-disabled": { color: "rgba(0,0,0,0.26)" },
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
}
